import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, forkJoin } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputTextModule } from 'primeng/inputtext';
import { Toast } from '@/shared/components/toast/toast';
import { TooltipModule } from 'primeng/tooltip';
import { BadgeModule } from 'primeng/badge';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { TableViewSwitcherComponent } from '@/shared/components/layout/table-view-switcher/table-view-switcher.component';
import { LayoutService } from '@/layout/service/layout.service';
import { IndicadoresService } from '@/core/api/sicom-api/financeiro/arrecadacao/indicadores/indicadores.service';
import { IndiceValorResponse, SalarioMinimoResponse } from '@/core/api/sicom-api/financeiro/arrecadacao/indicadores/indicadores.model';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { FiltroCampo } from '@/shared/components/layout/filtros-bar/filtro-campo';
import { FiltrosBar } from '@/shared/components/layout/filtros-bar/filtros-bar';
import { CardSortPills, CardSortOption } from '@/shared/components/layout/card-sort-pills/card-sort-pills';

type TipoIndicador = 'INPC' | 'SELIC' | 'SALARIO';

interface LinhaIndicador {
    chave: string;
    data: string;
    dataFim?: string;
    valor: number;
}

interface SortOpcao {
    label: string;
    field: 'data' | 'valor';
    direction: 'asc' | 'desc';
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-indicadores',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        TableModule,
        PaginatorModule,
        SelectModule,
        SelectButtonModule,
        InputTextModule,
        Toast,
        TooltipModule,
        BadgeModule,
        CardContent,
        CardContainer,
        TableViewSwitcherComponent,
        CanDirective,
        FiltrosBar,
        FiltroCampo,
        CardSortPills
    ],
    providers: [MessageService],
    templateUrl: './indicadores.html',
    styleUrl: './indicadores.scss'
})
/** Configuração dos indicadores do sistema. */
export class Indicadores implements OnInit {
    private readonly indicadoresService = inject(IndicadoresService);
    private readonly layoutService = inject(LayoutService);
    private readonly messageService = inject(MessageService);
    private readonly destroyRef = inject(DestroyRef);

    protected readonly rolePermissaoEditar = PERMISSOES.configuracoes.indicadores.editar;

    protected readonly tipo = signal<TipoIndicador>('INPC');
    protected readonly tiposIndicador = [
        { label: 'INPC', value: 'INPC' as TipoIndicador },
        { label: 'SELIC', value: 'SELIC' as TipoIndicador },
        { label: 'Salário Mínimo', value: 'SALARIO' as TipoIndicador }
    ];

    protected readonly inpc = signal<IndiceValorResponse[]>([]);
    protected readonly selic = signal<IndiceValorResponse[]>([]);
    protected readonly salario = signal<SalarioMinimoResponse[]>([]);

    protected readonly carregando = signal(false);
    protected readonly atualizando = signal(false);

    // ── Visualização, filtros e ordenação (padrão grid-pagamento) ──
    protected readonly currentView = signal<'table' | 'card'>(this.layoutService.getPresentationType());
    protected readonly filtroTexto = signal<string>('');
    protected readonly filtroAplicado = signal<string>('');
    protected readonly sortField = signal<SortOpcao['field']>('data');
    protected readonly sortOrder = signal<1 | -1>(-1);

    /**
     * Critérios das pills do modo card. Um por campo: a direção vem do clique na pill,
     * e não de duas opções separadas como no seletor que havia aqui.
     */
    protected readonly sortOpcoes: CardSortOption<SortOpcao['field']>[] = [
        { field: 'data', label: 'Data' },
        { field: 'valor', label: 'Valor' }
    ];

    // Linhas normalizadas do indicador selecionado
    protected readonly linhas = computed<LinhaIndicador[]>(() => {
        if (this.tipo() === 'SALARIO') {
            return this.salario().map((s) => ({
                chave: String(s.id),
                data: s.dt_inicio,
                dataFim: s.dt_fim,
                valor: s.vr_salario_minimo
            }));
        }
        const lista = this.tipo() === 'INPC' ? this.inpc() : this.selic();
        return lista.map((i) => ({
            chave: `${i.id_indice}-${i.dt_vigencia}`,
            data: i.dt_vigencia,
            valor: i.pe_valor
        }));
    });

    protected readonly linhasView = computed<LinhaIndicador[]>(() => {
        const termo = this.filtroAplicado().trim().toLowerCase();
        const field = this.sortField();
        const order = this.sortOrder();

        const filtradas = termo
            ? this.linhas().filter((l) => {
                  const alvo = `${this.formatarData(l.data)} ${this.formatarData(l.dataFim)} ${l.valor}`.toLowerCase();
                  return alvo.includes(termo);
              })
            : [...this.linhas()];

        return filtradas.sort((a, b) => {
            let cmp: number;
            if (field === 'valor') {
                cmp = (a.valor ?? 0) - (b.valor ?? 0);
            } else {
                cmp = String(a.data ?? '').localeCompare(String(b.data ?? ''));
            }
            return cmp * order;
        });
    });

    // ── Paginação ──────────────────────────────────────────────────
    protected readonly first = signal(0);
    protected readonly rows = signal(10);
    protected readonly totalRecords = computed(() => this.linhasView().length);
    protected readonly linhasPagina = computed<LinhaIndicador[]>(() => {
        const inicio = this.first();
        return this.linhasView().slice(inicio, inicio + this.rows());
    });

    ngOnInit(): void {
        this.carregar();
    }

    protected get ehSalario(): boolean {
        return this.tipo() === 'SALARIO';
    }

    protected onTipoChange(tipo: TipoIndicador): void {
        this.tipo.set(tipo);
        this.first.set(0);
        this.filtroTexto.set('');
        this.filtroAplicado.set('');
        this.sortField.set('data');
        this.sortOrder.set(-1);
    }

    protected formatarData(iso?: string | null): string {
        if (!iso) return '';
        const [ano, mes, dia] = iso.substring(0, 10).split('-');
        return ano && mes && dia ? `${dia}/${mes}/${ano}` : iso;
    }

    protected formatarValor(linha: LinhaIndicador): string {
        if (this.ehSalario) {
            return (linha.valor ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
        }
        return (linha.valor ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    }

    // ── Filtros / ordenação ────────────────────────────────────────
    protected aplicarFiltros(): void {
        this.filtroAplicado.set(this.filtroTexto());
        this.first.set(0);
    }

    protected limparFiltros(): void {
        this.filtroTexto.set('');
        this.filtroAplicado.set('');
        this.sortField.set('data');
        this.sortOrder.set(-1);
        this.first.set(0);
    }

    /** Trocar a ordenação volta para a primeira página: a página atual pode nem existir depois. */
    protected onSortCardChange(): void {
        this.first.set(0);
    }

    protected onSortChange(event: { field?: string | string[] | null; order?: number | null }): void {
        const field = (Array.isArray(event.field) ? event.field[0] : event.field) as SortOpcao['field'] | undefined;
        if (!field) return;
        const order: 1 | -1 = event.order === -1 ? -1 : 1;
        this.sortField.set(field);
        this.sortOrder.set(order);
        this.first.set(0);
    }

    protected onPageChange(event: PaginatorState): void {
        this.first.set(event.first ?? 0);
        this.rows.set(event.rows ?? this.rows());
    }

    // ── Ações ──────────────────────────────────────────────────────
    protected atualizarIndicadores(): void {
        this.atualizando.set(true);
        this.indicadoresService
            .atualizar()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.atualizando.set(false))
            )
            .subscribe({
                next: (res) => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Indicadores atualizados',
                        detail: `INPC: ${res.inpc_inseridos} novo(s) · SELIC: ${res.selic_inseridos} novo(s).`
                    });
                    this.carregar();
                },
                error: () => undefined
            });
    }

    private carregar(): void {
        this.carregando.set(true);
        forkJoin({
            inpc: this.indicadoresService.listarInpc(),
            selic: this.indicadoresService.listarSelic(),
            salario: this.indicadoresService.listarSalarioMinimo()
        })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.carregando.set(false))
            )
            .subscribe({
                next: (res) => {
                    this.inpc.set(res.inpc);
                    this.selic.set(res.selic);
                    this.salario.set(res.salario);
                    this.first.set(0);
                },
                error: () => {
                    this.inpc.set([]);
                    this.selic.set([]);
                    this.salario.set([]);
                }
            });
    }
}
